import React from "react";
import {
  Contact, ListChecks, FolderOpen, Gavel, ScrollText, GitPullRequest, Database, Plus, UserCircle,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { Drawer } from "../../components/Drawer";
import { Input, Field, Select } from "../../components/forms";
import { Grid, Stack, Row, KVGrid, SearchInput, StatusBadge, Timeline, Diff, Hint } from "./kit";
import { useHrms, useFetch, fmtDate, fullName, fmtMoney, JOINED_AT_BOUNDS } from "./useHrms";

// HRMS ▸ People — the master record. Live directory, HR profile editing,
// change-request queue, document verification and field configuration.

const TABS = [
  { key: "employees", label: "Employees", icon: <Contact size={14} /> },
  { key: "fields", label: "Profile fields", icon: <ListChecks size={14} /> },
  { key: "documents", label: "Documents", icon: <FolderOpen size={14} /> },
  { key: "discipline", label: "Discipline", icon: <Gavel size={14} /> },
  { key: "policies", label: "Policies", icon: <ScrollText size={14} /> },
  { key: "changes", label: "Change requests", icon: <GitPullRequest size={14} /> },
  { key: "import", label: "Import & data", icon: <Database size={14} /> },
];

export default function HrmsPeople() {
  const t = useDashTheme();
  const [tab, setTab] = React.useState("employees");

  return (
    <PageShell
      eyebrow="HRMS"
      title="People"
      subtitle="The employee master record — one row per person, one source of truth"
      icon={<Contact size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "employees" && <EmployeesTab t={t} />}
      {tab === "fields" && <FieldsTab t={t} />}
      {tab === "documents" && <DocumentsTab t={t} />}
      {tab === "discipline" && (
        <EmptyState title="Discipline register" description="Warnings and incident records ship in a later phase. Track via Workplace tickets (category: HR) for now." />
      )}
      {tab === "policies" && <PoliciesTab t={t} />}
      {tab === "changes" && <ChangesTab t={t} />}
      {tab === "import" && <ImportTab t={t} />}
    </PageShell>
  );
}

function EmployeesTab({ t }) {
  const { isHr, patch } = useHrms();
  const [search, setSearch] = React.useState("");
  const { data, loading, refresh } = useFetch(`/employees${search ? `?search=${encodeURIComponent(search)}` : ""}`);
  const [selected, setSelected] = React.useState(null);
  const detail = useFetch(selected ? `/employees/${selected.userId}` : null, [selected?.userId]);
  const [form, setForm] = React.useState({});

  const save = async () => {
    await patch(`/employees/${selected.userId}`, form);
    setForm({});
    detail.refresh(); refresh();
  };

  const rows = data?.employees || [];
  const u = detail.data?.user || {};
  const p = detail.data?.profile || {};

  return (
    <Stack gap={t.s[4]}>
      <Row justify="space-between" wrap>
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, ID, role" />
        <Badge tone="neutral">{rows.length} people</Badge>
      </Row>

      <SectionCard title="Employees" padding={0}>
        <DataTable
          loading={loading}
          onRowClick={(r) => setSelected(r)}
          columns={[
            {
              key: "name", header: "Employee",
              render: (r) => (
                <Row gap={10}>
                  <Avatar name={r.name} src={r.avatar} size={26} />
                  <div>
                    <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{r.name}</div>
                    <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{r.email}</div>
                  </div>
                </Row>
              ),
            },
            { key: "employeeId", header: "ID", render: (r) => r.employeeId || "—" },
            { key: "position", header: "Role", render: (r) => r.position || "—" },
            { key: "department", header: "Department", render: (r) => r.department || "—" },
            { key: "joinedAt", header: "Joined", render: (r) => fmtDate(r.joinedAt) },
            { key: "hrmsStatus", header: "Status", render: (r) => <StatusBadge value={cap(r.hrmsStatus)} /> },
          ]}
          rows={rows}
          emptyState={<EmptyState title="No employees" description="Team members appear here automatically." />}
        />
      </SectionCard>

      <Drawer
        open={!!selected} onClose={() => setSelected(null)}
        title={selected?.name} subtitle={selected?.position} icon={<UserCircle size={15} />} width={560}
      >
        {detail.loading ? <EmptyState title="Loading…" /> : (
          <Stack gap={t.s[4]}>
            <SectionCard title="Work" padding={16}>
              <KVGrid items={[
                { label: "Email", value: u.email },
                { label: "Employee ID", value: u.employeeId, mono: true },
                { label: "Reporting manager", value: fullName(u.reportingManager) },
                { label: "Joined", value: fmtDate(p?.joinedAt) },
                { label: "Type", value: cap(p?.employmentType) },
                { label: "Status", value: <StatusBadge value={cap(p?.status || "active")} /> },
                ...(isHr ? [{ label: "CTC (annual)", value: fmtMoney(p?.ctcAnnual), locked: true }] : []),
              ]} />
            </SectionCard>

            {isHr && p && (
              <SectionCard title="HR edit" subtitle="Direct write, audited per field" padding={16}>
                <Stack gap={t.s[3]}>
                  <Row gap={12} wrap>
                    <Field label="Employee ID"><Input defaultValue={u.employeeId || ""} onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))} /></Field>
                    <Field label="Position"><Input defaultValue={u.position || ""} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} /></Field>
                  </Row>
                  <Row gap={12} wrap>
                    <Field label="Status">
                      <Select defaultValue={p.status || "active"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                        {["active", "probation", "notice_period", "exited"].map((s) => <option key={s} value={s}>{cap(s)}</option>)}
                      </Select>
                    </Field>
                    <Field label="Joined">
                      <Input
                        type="date" min={JOINED_AT_BOUNDS.min} max={JOINED_AT_BOUNDS.max}
                        defaultValue={p.joinedAt ? String(p.joinedAt).slice(0, 10) : ""}
                        onChange={(e) => setForm((f) => ({ ...f, joinedAt: e.target.value }))}
                      />
                    </Field>
                  </Row>
                  <Row gap={12} wrap>
                    <Field label="PAN"><Input defaultValue={p.pan || ""} onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value }))} /></Field>
                    <Field label="Pay group"><Input defaultValue={p.payGroup || ""} onChange={(e) => setForm((f) => ({ ...f, payGroup: e.target.value }))} /></Field>
                  </Row>
                  <Row gap={8}>
                    <Button variant="primary" size="sm" onClick={save} disabled={!Object.keys(form).length}>Save changes</Button>
                  </Row>
                </Stack>
              </SectionCard>
            )}

            {(detail.data?.auditTrail || []).length > 0 && (
              <SectionCard title="Change history" padding={16}>
                <Timeline items={detail.data.auditTrail.slice(0, 10).map((a) => ({
                  what: a.what,
                  detail: a.field ? `${a.fromValue ?? "—"} → ${a.toValue ?? "—"}` : "",
                  when: fmtDate(a.createdAt),
                  by: a.changedBy ? fullName(a.changedBy) : "System",
                }))} />
              </SectionCard>
            )}
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

function FieldsTab({ t }) {
  const { isHr, post, patch } = useHrms();
  const { data, loading, refresh } = useFetch("/profile-fields");
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ label: "", fieldKey: "", group: "custom", type: "text", visibility: "everyone" });

  const add = async () => {
    if (!form.label.trim()) return;
    await post("/profile-fields", { ...form, fieldKey: form.fieldKey || form.label.toLowerCase().replace(/\s+/g, "_"), source: "custom" });
    setAdding(false);
    setForm({ label: "", fieldKey: "", group: "custom", type: "text", visibility: "everyone" });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <Hint>Standard fields map to the employee profile schema; custom fields extend it. Visibility controls who sees the value.</Hint>
      <SectionCard
        title="Profile fields" padding={0}
        actions={isHr && <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={() => setAdding(true)}>Add field</Button>}
      >
        <DataTable
          loading={loading}
          columns={[
            { key: "label", header: "Field" },
            { key: "group", header: "Group", render: (r) => cap(r.group) },
            { key: "type", header: "Type" },
            { key: "visibility", header: "Visible to", render: (r) => cap(String(r.visibility).replace(/_/g, " + ")) },
            { key: "source", header: "Source", render: (r) => <Badge tone={r.source === "standard" ? "neutral" : "info"}>{r.source}</Badge> },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr && r.source === "custom"
                ? <Button size="sm" variant="ghost" onClick={async () => { await patch(`/profile-fields/${r._id}`, { active: false }); refresh(); }}>Disable</Button>
                : null,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No fields configured" description="Run HR Settings → Initialize to seed the standard set." />}
        />
      </SectionCard>

      <Drawer open={adding} onClose={() => setAdding(false)} title="Add custom field" width={420}>
        <Stack gap={t.s[4]}>
          <Field label="Label"><Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="T-shirt size" /></Field>
          <Row gap={12}>
            <Field label="Group">
              <Select value={form.group} onChange={(e) => setForm((f) => ({ ...f, group: e.target.value }))}>
                {["personal", "work", "statutory", "custom"].map((g) => <option key={g} value={g}>{cap(g)}</option>)}
              </Select>
            </Field>
            <Field label="Type">
              <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {["text", "date", "select", "toggle", "number"].map((ty) => <option key={ty} value={ty}>{ty}</option>)}
              </Select>
            </Field>
          </Row>
          <Field label="Visibility">
            <Select value={form.visibility} onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value }))}>
              {["everyone", "hr_only", "hr_manager", "hr_payroll"].map((v) => <option key={v} value={v}>{cap(v.replace(/_/g, " + "))}</option>)}
            </Select>
          </Field>
          <Row gap={8}>
            <Button variant="primary" onClick={add}>Create</Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function DocumentsTab({ t }) {
  const { isHr, post } = useHrms();
  const docs = useFetch("/documents");
  const campaigns = useFetch("/document-requests");

  const verify = async (id, action) => {
    await post(`/documents/${id}/verify`, { action });
    docs.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Documents" subtitle={isHr ? "Org-wide, pending review first" : "Your scope"} padding={0}>
        <DataTable
          loading={docs.loading}
          columns={[
            { key: "name", header: "Document" },
            { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
            { key: "folder", header: "Folder" },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            {
              key: "file", header: "File", render: (r) => r.fileUrl
                ? <a href={r.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: t.font.size.sm, color: t.c.primary }}>View</a>
                : "—",
            },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr && r.status === "pending_review" ? (
                <Row gap={4} justify="flex-end">
                  <Button size="sm" variant="ghost" onClick={() => verify(r._id, "verify")}>Verify</Button>
                  <Button size="sm" variant="ghost" onClick={() => verify(r._id, "reject")}>Reject</Button>
                </Row>
              ) : null,
            },
          ]}
          rows={(docs.data || []).slice().sort((a, b) => (a.status === "pending_review" ? -1 : 1))}
          emptyState={<EmptyState title="No documents" />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Collection campaigns" subtitle="Ask a group for documents by a deadline" padding={0}>
          <DataTable
            loading={campaigns.loading}
            columns={[
              { key: "name", header: "Campaign" },
              { key: "due", header: "Due", render: (r) => fmtDate(r.dueDate) },
              { key: "progress", header: "Submitted", render: (r) => `${r.submitted} / ${r.audienceSize}` },
              { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            ]}
            rows={campaigns.data || []}
            emptyState={<EmptyState title="No campaigns" description="Create one from the API or a later UI phase." />}
          />
        </SectionCard>
      )}
    </Stack>
  );
}

function PoliciesTab({ t }) {
  const { isHr, post } = useHrms();
  const { data, loading, refresh } = useFetch("/announcements?kind=policy");
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({ title: "", category: "", version: "v1" });

  const add = async () => {
    if (!form.title.trim()) return;
    await post("/announcements", { ...form, kind: "policy" });
    setAdding(false); setForm({ title: "", category: "", version: "v1" });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard
        title="Policies" subtitle="Acknowledgement tracked per employee" padding={0}
        actions={isHr && <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={() => setAdding(true)}>Add policy</Button>}
      >
        <DataTable
          loading={loading}
          columns={[
            { key: "title", header: "Policy" },
            { key: "category", header: "Category", render: (r) => r.category || "—" },
            { key: "version", header: "Version", render: (r) => r.version || "—" },
            { key: "ack", header: "Acknowledged", render: (r) => `${r.ackPercent}%` },
            {
              key: "act", header: "", align: "right",
              render: (r) => !r.acknowledgedByMe
                ? <Button size="sm" variant="ghost" onClick={async () => { await post(`/announcements/${r._id}/acknowledge`, {}); refresh(); }}>Acknowledge</Button>
                : <Badge tone="positive">Read</Badge>,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No policies" description="Publish HR policies and track who has read them." />}
        />
      </SectionCard>

      <Drawer open={adding} onClose={() => setAdding(false)} title="Add policy" width={400}>
        <Stack gap={t.s[4]}>
          <Field label="Title"><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Leave policy" /></Field>
          <Row gap={12}>
            <Field label="Category"><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} /></Field>
            <Field label="Version"><Input value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} /></Field>
          </Row>
          <Row gap={8}>
            <Button variant="primary" onClick={add}>Publish</Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function ChangesTab({ t }) {
  const { isHr, post } = useHrms();
  const { data, loading, refresh } = useFetch("/change-requests");

  const decide = async (r, action) => {
    await post(`/approvals/${r._id}/decide`, { action });
    refresh();
  };

  return (
    <SectionCard title="Change requests" subtitle="Employee-submitted profile edits that need HR sign-off" padding={0}>
      <DataTable
        loading={loading}
        columns={[
          { key: "who", header: "Employee", render: (r) => (
            <Row gap={10}>
              <Avatar name={fullName(r.employeeId)} src={r.employeeId?.avatar} size={24} />
              <span>{fullName(r.employeeId)}</span>
            </Row>
          ) },
          {
            key: "changes", header: "Requested change",
            render: (r) => (
              <Stack gap={4}>
                {Object.entries(r.meta?.changes || {}).map(([k, v]) => (
                  <Diff key={k} from={k} to={String(typeof v === "object" ? JSON.stringify(v) : v)} />
                ))}
              </Stack>
            ),
          },
          { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
          { key: "createdAt", header: "Raised", render: (r) => fmtDate(r.createdAt) },
          {
            key: "act", header: "", align: "right",
            render: (r) => isHr && r.status === "pending" ? (
              <Row gap={4} justify="flex-end">
                <Button size="sm" variant="primary" onClick={() => decide(r, "approve")}>Approve</Button>
                <Button size="sm" variant="ghost" onClick={() => decide(r, "reject")}>Reject</Button>
              </Row>
            ) : null,
          },
        ]}
        rows={data || []}
        emptyState={<EmptyState title="No change requests" description="Employee edits to statutory fields land here." />}
      />
    </SectionCard>
  );
}

function ImportTab({ t }) {
  const { isHr, post } = useHrms();
  const [result, setResult] = React.useState(null);

  const initialize = async () => {
    const r = await post("/settings/initialize", {});
    setResult(r);
  };

  return (
    <Stack gap={t.s[4]}>
      <Hint>
        Employees sync automatically from Team members — there is no separate HRMS import.
        Bulk CSV import for profiles ships in a later phase; use the employee drawer for individual edits.
      </Hint>
      {isHr && (
        <SectionCard title="Bootstrap defaults" subtitle="Seed leave types, shifts, pay components, approval rules and profile fields. Idempotent — safe to re-run." padding={16}>
          <Row gap={12} wrap>
            <Button variant="primary" onClick={initialize}>Initialize HRMS defaults</Button>
            {result && (
              <Badge tone="positive">
                {Object.values(result.created).reduce((a, b) => a + b, 0)} new records created
              </Badge>
            )}
          </Row>
        </SectionCard>
      )}
    </Stack>
  );
}

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, " ") : "—");
